import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import MapPicker from "@/components/MapPicker";
import ShareButtons from "@/components/ShareButtons";
import AddressFields from "@/components/AddressFields";
import PublicIdBadge from "@/components/PublicIdBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Heart, MapPin, QrCode, Warning, House, Trash, ChatCircleDots, ShareNetwork, PencilSimple } from "@phosphor-icons/react";
import { DEPARTMENTS } from "@/lib/departments";

export default function DoudouDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [doudou, setDoudou] = useState(null);
  const [pickedPos, setPickedPos] = useState(null);
  const [showLost, setShowLost] = useState(false);
  const [lostAddress, setLostAddress] = useState({ street: "", postal_code: "", city: "" });
  const [lostCenter, setLostCenter] = useState(null);
  const [lostDepartment, setLostDepartment] = useState("");
  const [lostDeptTouched, setLostDeptTouched] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/connexion"); return; }
    fetchDoudou();
    // eslint-disable-next-line
  }, [user, loading, id]);

  // Auto-fill dept from postal code (only if the user hasn't manually overridden)
  useEffect(() => {
    if (lostDeptTouched) return;
    const cp = (lostAddress.postal_code || "").trim();
    if (/^\d{5}$/.test(cp)) {
      const dept = cp.startsWith("97") || cp.startsWith("98") ? cp.slice(0, 3) : cp.slice(0, 2);
      if (DEPARTMENTS.some((d) => d.code === dept)) setLostDepartment(dept);
    }
  }, [lostAddress.postal_code, lostDeptTouched]);

  const fetchDoudou = async () => {
    try {
      const { data } = await api.get(`/doudous/${id}`);
      setDoudou(data);
      if (data.lost_location) setPickedPos({ lat: data.lost_location.lat, lng: data.lost_location.lng });
    } catch { toast.error("Doudou introuvable"); navigate("/dashboard"); }
  };

  const declareLost = async () => {
    if (!pickedPos) { toast.error("Choisissez une position sur la carte"); return; }
    if (!lostDepartment) { toast.error("Merci de sélectionner un département (obligatoire)"); return; }
    const labelParts = [lostAddress.street, lostAddress.postal_code, lostAddress.city].filter(Boolean);
    const location_label = labelParts.join(", ");
    try {
      const { data } = await api.post(`/doudous/${id}/declare-lost`, {
        lat: pickedPos.lat, lng: pickedPos.lng, location_label, department: lostDepartment,
      });
      setDoudou(data);
      setShowLost(false);
      toast.success("Doudou déclaré perdu. Nous croisons les doigts !");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const markHome = async () => {
    try {
      const { data } = await api.post(`/doudous/${id}/mark-home`);
      setDoudou(data); setPickedPos(null);
      toast.success("Quel bonheur ! De retour à la maison.");
    } catch { toast.error("Erreur"); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cette fiche doudou ?")) return;
    await api.delete(`/doudous/${id}`);
    toast.success("Fiche supprimée");
    navigate("/dashboard");
  };

  if (!doudou) return <Layout><div className="max-w-4xl mx-auto p-12">Chargement...</div></Layout>;

  const isLost = doudou.status === "lost";

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-12">
        <Link to="/dashboard" className="text-stone-500 hover:text-orange-600 text-sm font-bold" data-testid="back-to-dashboard">← Retour</Link>
        <div className="grid md:grid-cols-[300px_1fr] gap-8 mt-4">
          <div className="aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-rose-100 to-orange-100 grid place-items-center" data-testid="doudou-photo">
            {doudou.photo ? <img src={doudou.photo} alt={doudou.name} className="w-full h-full object-contain" /> : <Heart size={100} weight="duotone" className="text-rose-300" />}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-4xl font-black text-stone-800" data-testid="doudou-name">{doudou.name}</h1>
              {isLost ? <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-sm font-bold" data-testid="doudou-status-lost">Perdu</span> : <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold" data-testid="doudou-status-home">À la maison</span>}
            </div>
            {doudou.public_id && (
              <div className="mt-4">
                <PublicIdBadge publicId={doudou.public_id} />
                <p className="text-stone-400 text-xs mt-1.5">Identifiant unique et permanent de {doudou.name}. Visible uniquement par vous.</p>
              </div>
            )}
            <p className="text-stone-600 mt-3 leading-relaxed">{doudou.description || "Aucune description"}</p>
            {doudou.department && <p className="text-stone-500 text-sm mt-3">Département : <strong>{doudou.department}</strong></p>}

            <div className="mt-6 flex flex-wrap gap-3">
              {!isLost ? (
                <Dialog open={showLost} onOpenChange={setShowLost}>
                  <DialogTrigger asChild>
                    <Button className="bg-rose-500 hover:bg-rose-600 rounded-full px-6 py-6 font-bold shadow-lg shadow-rose-500/30" data-testid="declare-lost-button">
                      <Warning size={20} weight="bold" className="mr-2" /> Déclarer perdu
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display text-2xl">Déclarer {doudou.name} perdu</DialogTitle></DialogHeader>
                    <p className="text-sm text-stone-600 mb-2">Indiquez l'adresse (facultatif) <strong>et</strong> cliquez sur la carte pour préciser la dernière position connue.</p>
                    <AddressFields value={lostAddress} onChange={setLostAddress} streetRequired={false}
                      onSelect={(s) => s.centre && setLostCenter({ lat: s.centre.coordinates[1], lng: s.centre.coordinates[0] })} />
                    <div className="mt-3">
                      <Label>Département *
                        <span className="text-xs text-stone-400 font-normal ml-1">(auto-détecté, modifiable)</span>
                      </Label>
                      <select
                        value={lostDepartment}
                        onChange={(e) => { setLostDepartment(e.target.value); setLostDeptTouched(true); }}
                        required
                        className="mt-1 w-full rounded-2xl border-2 border-rose-100 px-4 py-3 bg-white font-semibold focus:outline-none focus:border-orange-400"
                        data-testid="declare-lost-department-select"
                      >
                        <option value="">— Choisir un département —</option>
                        {DEPARTMENTS.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.name}</option>)}
                      </select>
                    </div>
                    <div className="mt-3">
                      <MapPicker value={pickedPos} onChange={setPickedPos} height={320} pinType="lost" centerOn={lostCenter} />
                    </div>
                    <Button onClick={declareLost} className="bg-orange-500 hover:bg-orange-600 rounded-full font-bold py-6 mt-2" data-testid="confirm-lost-button">
                      Confirmer la déclaration
                    </Button>
                  </DialogContent>
                </Dialog>
              ) : (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-6 py-6 font-bold" data-testid="mark-home-button">
                      <House size={20} weight="bold" className="mr-2" /> Retour à la maison
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-3xl">
                    <DialogHeader>
                      <DialogTitle className="font-display text-2xl text-stone-800">Confirmer les retrouvailles 🏠</DialogTitle>
                    </DialogHeader>
                    <p className="text-stone-600">
                      Formidable ! Confirmez-vous que <strong>{doudou.name}</strong> est bien de retour à la maison ?
                    </p>
                    <p className="text-xs text-stone-500 bg-emerald-50 rounded-2xl p-3 mt-2">
                      L'alerte publique sera retirée du site. Vous recevrez un email récapitulatif avec la possibilité de partager votre histoire.
                    </p>
                    <div className="flex gap-2 justify-end mt-4">
                      <DialogClose asChild>
                        <Button variant="outline" className="rounded-full font-bold" data-testid="mark-home-cancel-btn">Annuler</Button>
                      </DialogClose>
                      <DialogClose asChild>
                        <Button onClick={markHome} className="bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold" data-testid="mark-home-confirm">
                          <House size={18} weight="bold" className="mr-2" /> Oui, il est rentré !
                        </Button>
                      </DialogClose>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              {/* FEATURE FLAG: Étiquettes QR Code — désactivé pour MVP, ré-activer plus tard
              <Link to={`/doudou/${id}/etiquettes`} data-testid="order-stickers-link">
                <Button variant="outline" className="rounded-full px-6 py-6 border-2 border-orange-300 text-orange-700 font-bold hover:bg-orange-50">
                  <QrCode size={20} weight="bold" className="mr-2" /> Étiquettes QR
                </Button>
              </Link>
              */}
              <Link to={`/doudou/${id}/modifier`}>
                <Button variant="outline" className="rounded-full px-6 py-6 border-2 border-stone-300 text-stone-700 font-bold hover:bg-stone-50" data-testid="edit-doudou-button">
                  <PencilSimple size={20} weight="bold" className="mr-2" /> Modifier la fiche
                </Button>
              </Link>
              <Button onClick={handleDelete} variant="ghost" className="rounded-full text-stone-400 hover:text-rose-600" data-testid="delete-doudou-button">
                <Trash size={20} weight="bold" />
              </Button>
            </div>
          </div>
        </div>

        {isLost && doudou.lost_location && (
          <div className="mt-10">
            <h2 className="font-display text-2xl font-bold text-stone-800 flex items-center gap-2"><MapPin size={24} weight="duotone" className="text-orange-500" /> Dernière position connue</h2>
            <div className="mt-4">
              <MapPicker value={{ lat: doudou.lost_location.lat, lng: doudou.lost_location.lng }} readOnly height={360} />
            </div>
            <div className="mt-6 bg-gradient-to-br from-rose-50 to-orange-50 border-2 border-rose-100 rounded-3xl p-6" data-testid="share-section">
              <h3 className="font-display text-xl font-black text-stone-800 flex items-center gap-2">
                <ShareNetwork size={22} weight="duotone" className="text-rose-500" /> Diffusez l'alerte
              </h3>
              <p className="text-sm text-stone-600 mt-1.5 mb-4">
                Partagez l'alerte sur vos réseaux pour multiplier les chances de retrouver {doudou.name}. La page partagée est publique et ne révèle aucune information personnelle.
              </p>
              <ShareButtons
                url={`${window.location.origin}/alerte/${doudou.doudou_id}`}
                title={`🧸 ${doudou.name} a été perdu${doudou.department ? ` dans le ${doudou.department}` : ""}. Aidez-nous à le retrouver !`}
              />
              <p className="text-xs text-stone-500 mt-4 flex items-center gap-1.5">
                <ChatCircleDots size={14} weight="duotone" className="text-rose-400" />
                Lien direct : <code className="bg-white px-2 py-0.5 rounded-lg text-orange-600 ml-1 text-[11px]">{window.location.origin}/alerte/{doudou.doudou_id}</code>
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
